import {
  executionScopeToDto,
  mapAssetDownloadUrlDto,
  mapJobCancelResultDto,
  mapJobContinueResultDto,
  mapJobDetailDto,
  mapJobListDto,
  mapJobSubmitResultDto,
  type AssetDownloadUrl,
  type AssetDownloadUrlDto,
  type JobCancelResult,
  type JobCancelResultDto,
  type JobContinueResult,
  type JobContinueResultDto,
  type JobDetail,
  type JobDetailDto,
  type JobList,
  type JobListDto,
  type JobSubmitResult,
  type JobSubmitResultDto,
} from '@/types';

import type {
  IdempotencyOptions,
  JobContinueOptions,
  JobsService,
  JobSubmitRequest,
} from '../jobsService';

import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`JobsService`
 * -----------------------------------------------------------------------------
 * 4 endpoints;對齊後端 `/api/projects/{pid}/jobs/*`。
 *
 * **Auth**:cookie + `X-CSRF-Token`(`httpRequestWithRefresh` 自動帶;refresh chain
 * 也自動繼承)。長連線**不在這層做** — Job 進度由 `useJobListener` 走 Firestore
 * `onSnapshot` 推送,本 service 只供 submit / cold-start get / continue / cancel /
 * asset download URL 五個獨立 REST 操作。
 *
 * **Idempotency**:submit / continue / cancel 三個寫操作都帶 `X-Idempotency-Key`
 * header(caller 不給 → 內建 `crypto.randomUUID()`)。Backend 5 分鐘內同 key 直接
 * 回上次 response。
 *
 * @see plans/workflow-valiant-quilt.md — 階段 1 service 層
 */

const PATH = {
  submit: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/jobs`,
  // R274.1(docs/97 §1)— 生成紀錄清單(與 submit 同 path,GET + query)。
  list: (pid: string, limit?: number, cursor?: string | null) => {
    const q = new URLSearchParams();
    if (limit !== undefined) q.set('limit', String(limit));
    if (cursor) q.set('cursor', cursor);
    const qs = q.toString();
    return `/api/projects/${encodeURIComponent(pid)}/jobs${qs ? `?${qs}` : ''}`;
  },
  detail: (pid: string, jid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/jobs/${encodeURIComponent(jid)}`,
  continue: (pid: string, jid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/jobs/${encodeURIComponent(jid)}/continue`,
  cancel: (pid: string, jid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/jobs/${encodeURIComponent(jid)}/cancel`,
  // R28 — Asset signed download URL (docs/21)
  assetDownloadUrl: (pid: string, jid: string, aid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/jobs/${encodeURIComponent(jid)}/assets/${encodeURIComponent(aid)}/download-url`,
} as const;

/** caller 沒給 idempotency key → 在這裡產一個 fresh UUID。 */
function ensureIdempotencyKey(opts?: IdempotencyOptions): string {
  if (opts?.idempotencyKey) return opts.idempotencyKey;
  // crypto.randomUUID 在 Vite 5 + 現代瀏覽器 + jsdom 25 都有；test 端 setup.ts 已 polyfill
  return crypto.randomUUID();
}

function buildSubmitBody(req: JobSubmitRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {
    execution_scope: executionScopeToDto(req.executionScope),
    // R111(docs/67 §3.1)— **不帶 `graph`**:後端自 per-doc(nodes/edges collection)凍結,
    // 那才是 SSOT;帶了會走舊路徑,腳本藍卡 film_settings 進不了 job snapshot(FE graph
    // mapper 對 OUTPUT 不送 config)→ 全片級設定失效。凍結後後端照樣跑全套驗證
    // (edge 文法 / cycle / fan-in cap / merge clips 預檢 / config 白名單 / film_settings)。
  };
  if (req.brandKitId !== undefined && req.brandKitId !== null) {
    body.brand_kit_id = req.brandKitId;
  }
  // R397 — 醫美確認:**只在 true 時帶**(見 SubmitJobParams.complianceAck 的 forbid 說明)。
  if (req.complianceAck === true) {
    body.compliance_ack = true;
  }
  // R424 — 語系:**有值才上 wire**(舊 BE `extra="forbid"`,夾帶 = 整包 422;判準在呼叫端
  // `currentOutputLocale()`,這裡不自己讀 catalog / i18n)。
  if (req.locale) {
    body.locale = req.locale;
  }
  // ⚠️ 不要把 `reuse_completed` 加回來:BE `SubmitJobRequest`(extra="forbid")已於
  // 2026-07-16(ADR-034 批)移除該欄 —— R369 稽核前這裡還留著送出分支,是一顆
  // 「caller 一帶就 submit 422」的地雷(當時零活呼叫端,故從未炸)。
  return body;
}

function buildContinueBody(opts?: JobContinueOptions): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  // Round 50 — `regenerate_node_ids` 對 Stage B 是 explicit regeneration scope
  // (caller 主動指定要重做的 nodes);undefined 表示「BE 端決定 default behavior」。
  if (opts?.regenerateNodeIds !== undefined) {
    body.regenerate_node_ids = opts.regenerateNodeIds;
  }
  return body;
}

export function createHttpJobsService(config: HttpClientConfig): JobsService {
  return {
    async submit(projectId, req, opts): Promise<JobSubmitResult> {
      const key = ensureIdempotencyKey(opts);
      const env = await httpRequestWithRefresh<JobSubmitResultDto>(
        config,
        'POST',
        PATH.submit(projectId),
        buildSubmitBody(req),
        { extraHeaders: { 'X-Idempotency-Key': key } },
      );
      return mapJobSubmitResultDto(unwrapResult(env, 'jobs.submit'));
    },


    async list(projectId, opts): Promise<JobList> {
      const env = await httpRequestWithRefresh<JobListDto>(
        config,
        'GET',
        PATH.list(projectId, opts?.limit, opts?.cursor),
      );
      return mapJobListDto(unwrapResult(env, 'jobs.list'));
    },

    async get(projectId, jobId): Promise<JobDetail> {
      const env = await httpRequestWithRefresh<JobDetailDto>(
        config,
        'GET',
        PATH.detail(projectId, jobId),
      );
      return mapJobDetailDto(unwrapResult(env, 'jobs.get'));
    },

    async continue(projectId, jobId, opts): Promise<JobContinueResult> {
      const key = ensureIdempotencyKey(opts);
      const env = await httpRequestWithRefresh<JobContinueResultDto>(
        config,
        'POST',
        PATH.continue(projectId, jobId),
        buildContinueBody(opts),
        { extraHeaders: { 'X-Idempotency-Key': key } },
      );
      return mapJobContinueResultDto(unwrapResult(env, 'jobs.continue'));
    },

    async cancel(projectId, jobId, opts): Promise<JobCancelResult> {
      const key = ensureIdempotencyKey(opts);
      const env = await httpRequestWithRefresh<JobCancelResultDto>(
        config,
        'POST',
        PATH.cancel(projectId, jobId),
        {},
        { extraHeaders: { 'X-Idempotency-Key': key } },
      );
      return mapJobCancelResultDto(unwrapResult(env, 'jobs.cancel'));
    },

    async getAssetDownloadUrl(projectId, jobId, assetId): Promise<AssetDownloadUrl> {
      const env = await httpRequestWithRefresh<AssetDownloadUrlDto>(
        config,
        'GET',
        PATH.assetDownloadUrl(projectId, jobId, assetId),
      );
      return mapAssetDownloadUrlDto(unwrapResult(env, 'jobs.getAssetDownloadUrl'));
    },
  };
}
